export type InfoSectionId = 'about' | 'pricing' | 'privacy' | 'feedback';

export type InfoSection = {
  id: InfoSectionId;
  label: string;
  title: string;
  accent: string;
  path: string;
  paragraphs: string[];
  bullets?: string[];
  email?: string;
};

export const INFO_SECTION_ORDER: InfoSectionId[] = [
  'about',
  'pricing',
  'privacy',
  'feedback',
];

export const INFO_SECTIONS: Record<InfoSectionId, InfoSection> = {
  about: {
    id: 'about',
    label: 'About',
    title: 'About Homerun',
    accent: '#ffd86f',
    path: '/about',
    paragraphs: [
      'Homerun is a classroom-ready multiplayer learning platform built for active learning, rapid formative assessment, and high-energy teaching. Teachers create or curate question sets, launch a live session, and students join instantly to move their character toward the answer zone that matches their choice.',
      'The game is designed to separate reading and thinking time from commitment under pressure. That structure helps surface misconceptions in real time, gives teachers a clearer view of what the room is understanding, and turns answer-checking into a shared discussion about reasoning rather than a private guess.',
      'Homerun also supports AI-based solo practice, so students can rehearse the same style of question-driven gameplay outside the live classroom. While the current platform has been developed with legal education in mind, the wider aim is to make complex teaching more interactive, repeatable, and easier to run at scale.',
      'Version history',
      'Version 1 (2025): The first version of Homerun was designed and developed by Dr Armin Alimardani with support from generative AI tools.',
      'Version 2 (2026): technical architecture, frontend development, and backend/database integration continued under Armin Alimardani, while UX/UI design, gameplay systems design, playtesting and QA, balancing and difficulty tuning, accessibility design, audio/visual direction, deployment, and maintenance were shaped collaboratively by Dr Armin Alimardani, Dr Jacinta Sassine, Dr Bradley Gooding, Dr Shreeya Smith, Dr Sophia Bai, and Dr Kaitlyn Poole.',
    ],
  },
  pricing: {
    id: 'pricing',
    label: 'Pricing',
    title: 'Free for teachers and students',
    accent: '#8ef0c4',
    path: '/pricing',
    paragraphs: [
      'Homerun is currently free to use. Teachers can create question sets, run classroom games, and share sessions with students without paying for access.',
      'The platform is still relatively lean to operate, so the current goal is to keep it open and low-friction while usage remains manageable. If operating costs or usage patterns change in a way that affects this, any future pricing change would be communicated well in advance.',
    ],
    bullets: [
      'No charge for teacher accounts',
      'No charge for students joining live games',
      'No charge for current AI practice flows',
    ],
  },
  privacy: {
    id: 'privacy',
    label: 'Data & Privacy',
    title: 'Data and privacy',
    accent: '#9bc8ff',
    path: '/data-privacy',
    paragraphs: [
      'Teacher access is handled through Firebase Authentication. Homerun currently supports Google sign-in and email/password authentication for teacher accounts. Game data, teacher-authored content, and session records are stored in Google Firebase so the platform can run live games, AI practice sessions, and teacher dashboard workflows.',
      'Students do not need a full teacher-style account to participate. In normal classroom use, they join with a game code and the in-game details needed to run the session. Operational providers may collect standard service-level telemetry such as crash information, basic usage metrics, and infrastructure diagnostics needed to keep the platform reliable.',
      'Please do not upload sensitive, confidential, or personal material that you would not want stored in an online teaching tool. Homerun is built for classroom content and classroom-safe use.',
      'Teacher-authored game content is used to run the platform experience and support administration or troubleshooting when needed. It is not sold to third parties and is not used to train AI models.',
    ],
    email: 'teamsafetofailai@gmail.com',
  },
  feedback: {
    id: 'feedback',
    label: 'Feedback',
    title: 'Help shape what comes next',
    accent: '#ffb4d8',
    path: '/feedback',
    paragraphs: [
      'Share what is working, what is broken, and what would make Homerun more useful, more fun, or more classroom-friendly. Practical feedback from real teaching use is what drives the next round of improvements.',
      'If you hit a bug, want a feature, or have an idea for making the platform clearer for students or easier for teachers to run, send it through by email.',
    ],
    email: 'teamsafetofailai@gmail.com',
  },
};
